"use client";

import { c as _c } from "react/compiler-runtime";
import { format } from "date-fns";
import { ArrowRight, Lock, Maximize2, Minimize2, MousePointerClick, X } from "lucide-react";
import dynamic from "next/dynamic";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TooltipProvider } from "@/lib/components/tooltip";
import { cn } from "@/lib/utils";
import { DemoProvider, type DemoContextValue } from "./dashboard-demo/context";
import { findNavLink, isLiveView, type LiveView } from "./dashboard-demo/nav";
import { DEMO_ANCHOR_DAY, DEMO_OPENED_AT, DEMO_ORG, DEMO_PROJECTS, buildDailyActivity, type DemoProject } from "./dashboard-demo-data";
import { TrackedLink } from "./tracked-link";

// The demo body only ever renders after hydration, so its views, sidebar and
// recharts dependency load as their own chunk instead of shipping in the
// homepage's initial bundle.
const DemoBody = dynamic(() => import("./dashboard-demo/body").then(m => m.DemoBody), {
  ssr: false
});
interface Notice {
  id: number;
  message: string;
}
export function DashboardDemo() {
  const $ = _c(84);
  const posthog = usePostHog();
  const [view, setView] = useState("");
  const [project, setProject] = useState(DEMO_PROJECTS[0]);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      anchorDay: DEMO_ANCHOR_DAY,
      openedAt: DEMO_OPENED_AT
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [clock, setClock] = useState(t0);
  const [notice, setNotice] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const [windowFullscreen, setWindowFullscreen] = useState(false);
  const frameRef = useRef(null);
  const mainRef = useRef(null);
  const noticeTimer = useRef(undefined);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const now = new Date();
      setClock({
        anchorDay: format(now, "yyyy-MM-dd"),
        openedAt: now.getTime()
      });
      setHydrated(true);
      return () => clearTimeout(noticeTimer.current);
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== clock.anchorDay || $[4] !== project) {
    t3 = buildDailyActivity(clock.anchorDay, project);
    $[3] = clock.anchorDay;
    $[4] = project;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const history = t3;
  let t4;
  if ($[6] !== posthog) {
    t4 = (action, value) => {
      posthog.capture("hero_demo_interacted", {
        action,
        value
      });
    };
    $[6] = posthog;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const track = t4;
  const fullscreen = nativeFullscreen || windowFullscreen;
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const sync = () => setNativeFullscreen(document.fullscreenElement === frameRef.current);
      document.addEventListener("fullscreenchange", sync);
      return () => document.removeEventListener("fullscreenchange", sync);
    };
    t6 = [];
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== windowFullscreen) {
    t7 = () => {
      if (!windowFullscreen) {
        return;
      }
      const root = document.documentElement;
      const previous = root.style.overflow;
      root.style.overflow = "hidden";
      const onKey = event => {
        if (event.key === "Escape") {
          setWindowFullscreen(false);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => {
        root.style.overflow = previous;
        window.removeEventListener("keydown", onKey);
      };
    };
    t8 = [windowFullscreen];
    $[10] = windowFullscreen;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      setWindowFullscreen(false);
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const exitFullscreen = t9;
  let t10;
  if ($[14] !== fullscreen || $[15] !== track) {
    t10 = () => {
      if (fullscreen) {
        exitFullscreen();
        track("fullscreen", "exit");
        return;
      }
      track("fullscreen", "enter");
      const frame = frameRef.current;
      if (frame?.requestFullscreen) {
        frame.requestFullscreen().catch(() => setWindowFullscreen(true));
        return;
      }
      setWindowFullscreen(true);
    };
    $[14] = fullscreen;
    $[15] = track;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const toggleFullscreen = t10;
  let t11;
  if ($[17] !== track || $[18] !== view) {
    t11 = () => {
      setUnlocked(true);
      track("start", view || "dashboard");
    };
    $[17] = track;
    $[18] = view;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const start = t11;
  let t12;
  if ($[20] !== track || $[21] !== view) {
    t12 = () => {
      exitFullscreen();
      setMobileNavOpen(false);
      setNotice(null);
      setUnlocked(false);
      track("lock", view || "dashboard");
    };
    $[20] = track;
    $[21] = view;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const lock = t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = message => {
      clearTimeout(noticeTimer.current);
      setNotice(current => ({
        id: (current?.id ?? 0) + 1,
        message
      }));
      noticeTimer.current = setTimeout(() => setNotice(null), 4000);
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const notify = t13;
  let t14;
  if ($[24] !== track) {
    t14 = target => {
      if (isLiveView(target)) {
        setView(target);
        setMobileNavOpen(false);
        mainRef.current?.scrollTo({
          top: 0
        });
        track("view", target || "dashboard");
        return;
      }
      track("view_unavailable", target);
      notify(`${findNavLink(target)?.label ?? "This page"} isn't part of this demo.`);
    };
    $[24] = track;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const navigate = t14;
  let t15;
  if ($[26] !== track) {
    t15 = next => {
      setProject(next);
      track("project", next.id);
    };
    $[26] = track;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== clock.anchorDay || $[29] !== clock.openedAt || $[30] !== history || $[31] !== navigate || $[32] !== project || $[33] !== t15 || $[34] !== track || $[35] !== view) {
    t16 = {
      view,
      navigate,
      notify,
      track,
      openedAt: clock.openedAt,
      anchorDay: clock.anchorDay,
      project,
      selectProject: t15,
      history
    };
    $[28] = clock.anchorDay;
    $[29] = clock.openedAt;
    $[30] = history;
    $[31] = navigate;
    $[32] = project;
    $[33] = t15;
    $[34] = track;
    $[35] = view;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const context = t16;
  let t17;
  if ($[37] !== project || $[38] !== view) {
    t17 = view.startsWith("org/") ? `${DEMO_ORG.id}/${view}` : `${DEMO_ORG.id}/${project.id}${view ? `/${view}` : ""}`;
    $[37] = project;
    $[38] = view;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const path = t17;
  const t18 = windowFullscreen || undefined;
  const t19 = fullscreen ? "h-dvh w-full" : "rounded-2xl border shadow-[0_40px_100px_-50px_rgba(17,17,19,0.45)] dark:shadow-[0_40px_120px_-50px_rgba(0,0,0,0.9)]";
  const t20 = windowFullscreen && "fixed inset-0 z-[100]";
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = cn("@container/demo flex flex-col overflow-hidden border-black/10 bg-background text-foreground dark:border-white/10", t19, t20);
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div aria-hidden={true} className="flex shrink-0 gap-1.5"><span className="size-2.5 rounded-full bg-foreground/15" /><span className="size-2.5 rounded-full bg-foreground/15" /><span className="size-2.5 rounded-full bg-foreground/15" /></div>;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Lock className="size-3 shrink-0" />;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== path) {
    t24 = <div className="mx-auto hidden min-w-0 max-w-md flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-1 font-mono text-[11px] text-muted-foreground @xl/demo:flex">{t23}<span className="truncate">llmgateway.io/dashboard/{path}</span></div>;
    $[45] = path;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="ml-auto hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground @md/demo:inline @xl/demo:ml-0">Sample data · read-only</span>;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== fullscreen || $[49] !== lock || $[50] !== toggleFullscreen || $[51] !== unlocked) {
    t26 = unlocked && <div className="ml-auto flex shrink-0 items-center gap-1 @md/demo:ml-0"><TrackedLink href="/signup" auth={true} location="home_demo_chrome" cta="get_api_key" className="inline-flex h-7 items-center gap-1.5 rounded-md bg-foreground px-2.5 text-xs font-semibold text-background transition-colors hover:bg-foreground/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Get started<ArrowRight className="size-3.5" /></TrackedLink><button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? "Exit full screen" : "Full screen"} className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{fullscreen ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}<span className="hidden @xl/demo:inline">{fullscreen ? "Exit full screen" : "Full screen"}</span></button><button type="button" onClick={lock} aria-label="Lock demo" className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Lock className="size-3.5" /><span className="hidden @xl/demo:inline">Lock</span></button></div>;
    $[48] = fullscreen;
    $[49] = lock;
    $[50] = toggleFullscreen;
    $[51] = unlocked;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t24 || $[54] !== t26) {
    t27 = <div className="flex h-10 items-center gap-3 border-b border-border bg-muted/50 px-3">{t22}{t24}{t25}{t26}</div>;
    $[53] = t24;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const t28 = !unlocked;
  const t29 = fullscreen ? "h-full" : "h-[560px] md:h-[680px]";
  let t30;
  if ($[56] !== t29) {
    t30 = cn("relative flex bg-sidebar", t29);
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== collapsed || $[59] !== hydrated || $[60] !== mobileNavOpen || $[61] !== track) {
    t31 = hydrated && <DemoBody collapsed={collapsed} onToggleSidebar={() => {
      setCollapsed(!collapsed);
      track("sidebar", collapsed ? "expand" : "collapse");
    }} mobileNavOpen={mobileNavOpen} onOpenMobileNav={() => setMobileNavOpen(true)} onCloseMobileNav={() => setMobileNavOpen(false)} mainRef={mainRef} />;
    $[58] = collapsed;
    $[59] = hydrated;
    $[60] = mobileNavOpen;
    $[61] = track;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== notice) {
    t32 = notice && <div key={notice.id} role="status" className="absolute inset-x-3 bottom-3 z-50 flex items-center gap-3 rounded-lg border border-border bg-popover px-4 py-2.5 text-sm text-popover-foreground shadow-lg animate-in fade-in-0 slide-in-from-bottom-2 @xl/demo:inset-x-auto @xl/demo:left-1/2 @xl/demo:-translate-x-1/2"><span className="min-w-0 flex-1 @xl/demo:whitespace-nowrap">{notice.message}</span><TrackedLink href="/signup" auth={true} location="home_demo" cta="demo_notice_signup" className="shrink-0 font-medium underline underline-offset-4">Try it free</TrackedLink><button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"><X className="h-4 w-4" /></button></div>;
    $[63] = notice;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== t28 || $[66] !== t30 || $[67] !== t31 || $[68] !== t32) {
    t33 = <div inert={t28} className={t30}>{t31}{t32}</div>;
    $[65] = t28;
    $[66] = t30;
    $[67] = t31;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== start || $[71] !== unlocked) {
    t34 = !unlocked && <button type="button" onClick={start} className="group absolute inset-0 z-[60] flex items-center justify-center bg-gradient-to-b from-background/10 via-background/30 to-background/70 focus-visible:outline-none"><span className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-background/90 px-6 py-5 text-center shadow-xl backdrop-blur-sm transition-transform group-hover:-translate-y-0.5 group-focus-visible:ring-2 group-focus-visible:ring-ring"><span className="text-base font-semibold">Explore the dashboard</span><span className="max-w-64 text-sm text-muted-foreground">A read-only copy with sample data. Click through every page.</span><span className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background"><MousePointerClick className="size-4" />Start demo</span></span></button>;
    $[70] = start;
    $[71] = unlocked;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t33 || $[74] !== t34) {
    t35 = <div className="relative min-h-0 flex-1">{t33}{t34}</div>;
    $[73] = t33;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t18 || $[77] !== t21 || $[78] !== t27 || $[79] !== t35) {
    t36 = <TooltipProvider delayDuration={0}><div ref={frameRef} data-window-fullscreen={t18} className={t21}>{t27}{t35}</div></TooltipProvider>;
    $[76] = t18;
    $[77] = t21;
    $[78] = t27;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== context || $[82] !== t36) {
    t37 = <DemoProvider value={context}>{t36}</DemoProvider>;
    $[81] = context;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  return t37;
}